import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { getAllJobs, saveJob } from "../utils/jobStorage";
import FeedsSpeedsCalculator from "../components/calculators/FeedsSpeedsCalculator";
import TapDrillCalculator from "../components/calculators/TapDrillCalculator";
import ThreadPitchCalculator from "../components/calculators/ThreadPitchCalculator";
import HoleSizingCalculator from "../components/calculators/HoleSizingCalculator";
import UnitConversionCalculator from "../components/calculators/UnitConversionCalculator";
import ShopMathCalculator from "../components/calculators/ShopMathCalculator";
import TrigCalculator from "../components/calculators/TrigCalculator";
import BoltPatternCalculator from "../components/calculators/BoltPatternCalculator";
import GearRatioCalculator from "../components/calculators/GearRatioCalculator";
import ToolDeflectionCalculator from "../components/calculators/ToolDeflectionCalculator";
import ToolWearCalculator from "../components/calculators/ToolWearCalculator";
import ToolCostPerInchCalculator from "../components/calculators/ToolCostPerInchCalculator";
import GCodeCalculator from "../components/calculators/GCodeCalculator";
import GDTCalculator from "../components/calculators/GDTCalculator";
import ExpandedShopCalculator from "../components/calculators/ExpandedShopCalculator";
import ManufacturingCalculator from "../components/calculators/ManufacturingCalculator";
import ProfessionalCalculator from "../components/calculators/ProfessionalCalculator";
import {
  CALCULATOR_TOOLS,
  getCalculatorCategoryName,
  getCalculatorLabel,
} from "../data/calculatorCatalog";
import { FORMULA_REFERENCE_DATABASE } from "../data/formulaReferenceDatabase";
import { PROFESSIONAL_CALCULATOR_LABELS } from "../data/professionalCalculatorRegistry";
import { recordRecentTool } from "../utils/workspacePreferences";

import "./Calculator.css";

class CalculatorErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: String(error?.message || error || "Unknown calculator error"),
    };
  }

  componentDidUpdate(previousProps) {
    if (this.state.hasError && previousProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, message: "" });
    }
  }

  componentDidCatch(error, info) {
    console.error("ManuCalc calculator error:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="calculator-panel">
          <div className="warnings">
            <h4>Calculator Failed To Load</h4>
            <p>
              This calculator hit a runtime error. The rest of ManuCalc is still
              available, and this message gives us the exact failure path to fix.
            </p>
            <code>{this.state.message}</code>
          </div>
          <div className="calculator-actions">
            <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
              Refresh Calculator
            </button>
            <Link className="btn btn-secondary" to="/dashboard">
              Back to Calculators
            </Link>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const Calculator = ({ user, rolePermissions = {} }) => {
  const { toolId } = useParams();
  const location = useLocation();
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [selectedPartId, setSelectedPartId] = useState("");
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [savingCalculation, setSavingCalculation] = useState(false);
  const [workspaceMessage, setWorkspaceMessage] = useState("");
  const [planSearch, setPlanSearch] = useState("");
  const canExport = rolePermissions?.canExport === true;

  const calculatorComponents = {
    "feeds-speeds": FeedsSpeedsCalculator,
    "rpm": FeedsSpeedsCalculator,
    "gear-ratio": GearRatioCalculator,
    "tool-deflection": ToolDeflectionCalculator,
    "tool-wear": ToolWearCalculator,
    "tap-drill": TapDrillCalculator,
    "threading": ThreadPitchCalculator,
    "hole-sizing": HoleSizingCalculator,
    "unit-conversion": UnitConversionCalculator,
    "shop-math": ShopMathCalculator,
    "trig": TrigCalculator,
    "bolt-pattern": BoltPatternCalculator,
    "tool-cost-per-inch": ToolCostPerInchCalculator,
    "g-code": GCodeCalculator,
    "gdt": GDTCalculator,
    taper: ExpandedShopCalculator,
    "drill-depth": ExpandedShopCalculator,
    counterbore: ExpandedShopCalculator,
    countersink: ExpandedShopCalculator,
    "bore-interpolation": ExpandedShopCalculator,
    knurl: ExpandedShopCalculator,
    "true-position": ExpandedShopCalculator,
    horsepower: ExpandedShopCalculator,
    "bend-allowance": ManufacturingCalculator,
    "k-factor": ManufacturingCalculator,
    "press-brake-tonnage": ManufacturingCalculator,
    "kerf-compensation": ManufacturingCalculator,
    "weld-heat-input": ManufacturingCalculator,
    "weld-cost": ManufacturingCalculator,
    "material-weight": ManufacturingCalculator,
    "print-cost": ManufacturingCalculator,
    "casting-shrinkage": ManufacturingCalculator,
    "job-quote": ManufacturingCalculator,
    "tolerance-stack": ManufacturingCalculator,
    "process-capability": ManufacturingCalculator,
    oee: ManufacturingCalculator,
    "takt-capacity": ManufacturingCalculator,
    "thermal-expansion": ManufacturingCalculator,
    "sheet-yield": ManufacturingCalculator,
    "hydraulic-force": ManufacturingCalculator,
    "inventory-reorder": ManufacturingCalculator,
    "3s-audit": ManufacturingCalculator,
    "red-tag-recovery": ManufacturingCalculator,
    ...Object.fromEntries(
      Object.keys(PROFESSIONAL_CALCULATOR_LABELS).map((id) => [id, ProfessionalCalculator])
    ),
  };

  const calculatorLabels = useMemo(() => ({
    "feeds-speeds": "Feeds, Speeds & RPM",
    "rpm": "Feeds, Speeds & RPM",
    "gear-ratio": "Gear Ratio / Gear Cutting",
    "tool-deflection": "Tool Deflection",
    "tool-wear": "Tool Wear",
    "tap-drill": "Tap Drill",
    "threading": "Thread Pitch",
    "hole-sizing": "Hole Clearance",
    "unit-conversion": "Unit Conversion",
    "shop-math": "Shop Math",
    "trig": "Trig",
    "bolt-pattern": "Bolt Pattern",
    "tool-cost-per-inch": "Tool Cost Per Inch Cut",
    "g-code": "G-code Starter",
    "gdt": "GD&T Helper",
    ...PROFESSIONAL_CALCULATOR_LABELS,
  }), []);

  const CalculatorComponent = calculatorComponents[toolId];
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  useEffect(() => {
    setLoadingPlans(true);
    setSaveError("");

    try {
      const loadedPlans = getAllJobs();
      setPlans(loadedPlans);
      const requestedPlanId =
        queryParams.get("manufacturingPlanId") ||
        queryParams.get("planId") ||
        queryParams.get("jobId") ||
        "";

      setSelectedPlanId((currentValue) => {
        if (requestedPlanId && loadedPlans.some((plan) => plan.id === requestedPlanId)) {
          return requestedPlanId;
        }
        if (currentValue && loadedPlans.some((plan) => plan.id === currentValue)) {
          return currentValue;
        }
        return loadedPlans[0]?.id || "";
      });
    } catch (error) {
      console.error("Failed to load manufacturing plans for calculator save:", error);
      setSaveError("Failed to load manufacturing plans.");
      setPlans([]);
      setSelectedPlanId("");
    } finally {
      setLoadingPlans(false);
    }
  }, [queryParams]);

  const selectedPlan = useMemo(() => {
    return plans.find((plan) => plan.id === selectedPlanId) || null;
  }, [plans, selectedPlanId]);

  const filteredPlans = useMemo(() => {
    const query = planSearch.trim().toLowerCase();
    if (!query) return plans;

    return plans.filter((plan) => {
      const text = [
        plan.name,
        plan.customer,
        plan.partNumber,
        ...(plan.parts || []).flatMap((part) => [
          part.name,
          part.partNumber,
          part.notes,
        ]),
      ].join(" ").toLowerCase();

      return text.includes(query);
    });
  }, [plans, planSearch]);

  const selectablePlans = useMemo(() => {
    if (!selectedPlan || filteredPlans.some((plan) => plan.id === selectedPlan.id)) {
      return filteredPlans;
    }
    return [selectedPlan, ...filteredPlans];
  }, [filteredPlans, selectedPlan]);

  const selectedPart = useMemo(() => {
    return (selectedPlan?.parts || []).find((part) => part.id === selectedPartId) || null;
  }, [selectedPlan, selectedPartId]);

  const activeTool = useMemo(() => {
    return CALCULATOR_TOOLS.find((tool) => tool.id === toolId) || null;
  }, [toolId]);

  const relatedTools = useMemo(() => {
    if (!activeTool) return [];
    return CALCULATOR_TOOLS.filter((tool) => {
      if (tool.id === activeTool.id) return false;
      return tool.categories.some((category) =>
        activeTool.categories.includes(category)
      );
    }).slice(0, 6);
  }, [activeTool]);

  const relatedFormulas = useMemo(() => {
    if (!activeTool) return [];
    const searchText = [
      activeTool.id,
      activeTool.name,
      activeTool.description,
      ...(activeTool.categories || []),
      ...(activeTool.tags || []),
    ].join(" ").toLowerCase();
    return FORMULA_REFERENCE_DATABASE.filter((formula) => {
      const formulaText = [
        formula.name,
        formula.category,
        formula.formula,
        formula.notes,
        ...(formula.relatedCalculators || []),
        ...(formula.tags || []),
      ].join(" ").toLowerCase();
      return (
        formula.relatedCalculators?.includes(activeTool.id) ||
        activeTool.categories?.some((category) => formulaText.includes(category.replace("-", " "))) ||
        formula.tags?.some((tag) => searchText.includes(String(tag).toLowerCase()))
      );
    }).slice(0, 5);
  }, [activeTool]);

  useEffect(() => {
    if (user?.uid && toolId) {
      recordRecentTool(user.uid, toolId, {
        name: calculatorLabels[toolId] || getCalculatorLabel(toolId),
        route: `/calculator/${toolId}`,
        type: "calculator",
      });
    }
  }, [calculatorLabels, toolId, user]);

  const selectedPlanCalculations = useMemo(() => {
    return (selectedPlan?.calculations || [])
      .slice()
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [selectedPlan]);

  const currentToolCalculationCount = useMemo(() => {
    return selectedPlanCalculations.filter((calculation) => calculation.toolId === toolId).length;
  }, [selectedPlanCalculations, toolId]);

  useEffect(() => {
    const parts = selectedPlan?.parts || [];
    const requestedPartId =
      queryParams.get("manufacturingPartId") ||
      queryParams.get("partId") ||
      "";

    if (!parts.length) {
      setSelectedPartId("");
      return;
    }
    if (requestedPartId && parts.some((part) => part.id === requestedPartId)) {
      setSelectedPartId(requestedPartId);
      return;
    }
    setSelectedPartId((currentValue) =>
      currentValue && parts.some((part) => part.id === currentValue)
        ? currentValue
        : parts[0].id
    );
  }, [queryParams, selectedPlan]);

  const normalizeSavePayload = (payload) => {
    if (!payload) {
      return {
        summary: `${calculatorLabels[toolId] || getCalculatorLabel(toolId)} calculation`,
        rawData: null,
      };
    }

    if (typeof payload === "string") {
      return {
        summary: payload,
        rawData: { text: payload },
      };
    }

    if (typeof payload === "object") {
      return {
        summary:
          payload.summary ||
          payload.title ||
          `${calculatorLabels[toolId] || getCalculatorLabel(toolId)} calculation`,
        rawData: payload,
      };
    }

    return {
      summary: `${calculatorLabels[toolId] || getCalculatorLabel(toolId)} calculation`,
      rawData: { value: payload },
    };
  };

  const handleSaveCalculation = async (payload) => {
    if (!selectedPlanId || !selectedPlan) {
      setSaveError("Select a manufacturing plan before saving a calculation.");
      setSaved(false);
      return;
    }

    try {
      setSavingCalculation(true);
      setSaveError("");
      setSaved(false);

      const normalized = normalizeSavePayload(payload);
      const timestamp = new Date().toISOString();
      const calculationRecord = {
        id: `calc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        toolId,
        toolName: calculatorLabels[toolId] || getCalculatorLabel(toolId),
        planId: selectedPlanId,
        planName: selectedPlan.name || "",
        customer: selectedPlan.customer || "",
        partId: selectedPart?.id || "",
        partName: selectedPart?.name || "",
        summary: normalized.summary,
        data: normalized.rawData,
        createdAt: timestamp,
        updatedAt: timestamp,
      };

      const updatedPlan = saveJob({
        ...selectedPlan,
        calculations: [...(selectedPlan.calculations || []), calculationRecord],
        calculatorValuesCount: (selectedPlan.calculatorValuesCount || 0) + 1,
        updatedAt: timestamp,
      });

      setPlans((currentPlans) =>
        currentPlans.map((plan) => (plan.id === updatedPlan.id ? updatedPlan : plan))
      );
      recordRecentTool(user?.uid, toolId, {
        name: calculatorLabels[toolId] || getCalculatorLabel(toolId),
        route: `/calculator/${toolId}`,
        type: "saved-calculation",
      });
      setSaved(true);

      window.setTimeout(() => {
        setSaved(false);
      }, 2500);
    } catch (error) {
      console.error("Failed to save calculation to manufacturing plan:", error);
      setSaveError("Failed to save calculation to manufacturing plan.");
      setSaved(false);
    } finally {
      setSavingCalculation(false);
    }
  };

  const showWorkspaceMessage = (message) => {
    setWorkspaceMessage(message);
    window.setTimeout(() => setWorkspaceMessage(""), 2400);
  };

  const copyCurrentToolLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showWorkspaceMessage("Calculator link copied.");
    } catch {
      showWorkspaceMessage(url);
    }
  };

  const exportPlanCalculations = (format) => {
    if (!canExport) {
      showWorkspaceMessage("Export permission is disabled for this account. Ask the company admin to enable Can Export.");
      return;
    }

    if (!selectedPlan || selectedPlanCalculations.length === 0) {
      showWorkspaceMessage("No saved calculations to export for this plan.");
      return;
    }

    const fileSafeName = String(selectedPlan.name || "manufacturing-plan")
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-|-$/g, "")
      .toLowerCase();

    let body = "";
    let type = "application/json";
    let extension = "json";

    if (format === "csv") {
      const rows = [
        ["Tool", "Summary", "Part", "Customer", "Created At"],
        ...selectedPlanCalculations.map((calculation) => [
          calculation.toolName || calculation.toolId || "",
          calculation.summary || "",
          calculation.partName || "",
          calculation.customer || selectedPlan.customer || "",
          calculation.createdAt || "",
        ]),
      ];
      body = rows
        .map((row) =>
          row
            .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
            .join(",")
        )
        .join("\n");
      type = "text/csv";
      extension = "csv";
    } else {
      body = JSON.stringify(
        {
          planId: selectedPlan.id,
          planName: selectedPlan.name,
          customer: selectedPlan.customer,
          exportedAt: new Date().toISOString(),
          calculations: selectedPlanCalculations,
        },
        null,
        2
      );
    }

    const blob = new Blob([body], { type });
    const downloadUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = downloadUrl;
    anchor.download = `${fileSafeName || "manufacturing-plan"}-calculations.${extension}`;
    anchor.click();
    URL.revokeObjectURL(downloadUrl);
    showWorkspaceMessage(`Exported ${selectedPlanCalculations.length} saved calculations.`);
  };

  if (!CalculatorComponent) {
    return (
      <div className="calculator-page">
        <div className="calculator-container">
          <h1>Calculator Not Found</h1>
          <p>The requested calculator tool is not available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="calculator-page">
      <div className="calculator-container">
        <section className="calculator-workspace-panel">
          <div className="calculator-workspace-main">
            <div>
              <span className="calculator-workspace-kicker">
                {activeTool?.categories?.map(getCalculatorCategoryName).join(" / ") || "Calculator"}
              </span>
              <h2>{calculatorLabels[toolId] || getCalculatorLabel(toolId)}</h2>
              <p>
                {activeTool?.description ||
                  "Run this calculator, save the output to a job/customer plan, then export or review the calculation record later."}
              </p>
            </div>

            {loadingPlans ? (
              <div className="calculator-workspace-muted">
                Loading plans...
              </div>
            ) : plans.length === 0 ? (
              <div className="calculator-workspace-muted">
                No manufacturing plans found. Create a plan in Manufacturing Plans first.
              </div>
            ) : (
              <div className="calculator-save-grid">
                <label>
                  Search Plans
                  <input
                    type="search"
                    value={planSearch}
                    onChange={(e) => setPlanSearch(e.target.value)}
                    placeholder="Search job, customer, part..."
                  />
                </label>
                <label>
                  Job / Customer Plan
                <select
                  value={selectedPlanId}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                >
                  {selectablePlans.length === 0 && (
                    <option value="">No matching plans</option>
                  )}
                  {selectablePlans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} {plan.customer ? `- ${plan.customer}` : ""}
                    </option>
                  ))}
                </select>
                </label>

                <label>
                  Part
                <select
                  value={selectedPartId}
                  onChange={(e) => setSelectedPartId(e.target.value)}
                  disabled={!selectedPlan?.parts?.length}
                >
                  {!selectedPlan?.parts?.length && (
                    <option value="">No parts in selected plan</option>
                  )}
                  {(selectedPlan?.parts || []).map((part) => (
                    <option key={part.id} value={part.id}>
                      {part.name || part.partNumber || "Part"}
                    </option>
                  ))}
                </select>
                </label>
              </div>
            )}
          </div>

          <aside className="calculator-workspace-side">
            <div className="calculator-workspace-stat">
              <span>Saved on plan</span>
              <strong>{selectedPlanCalculations.length}</strong>
            </div>
            <div className="calculator-workspace-stat">
              <span>This tool</span>
              <strong>{currentToolCalculationCount}</strong>
            </div>
            <div className="calculator-workspace-actions">
              <button type="button" className="btn btn-secondary" onClick={copyCurrentToolLink}>
                Copy Link
              </button>
              {selectedPlan && (
                <Link className="btn btn-secondary" to={`/jobs/${selectedPlan.id}`}>
                  View Plan
                </Link>
              )}
              <button type="button" className="btn btn-secondary" onClick={() => exportPlanCalculations("csv")}>
                Export CSV
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => exportPlanCalculations("json")}>
                Export JSON
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  if (!canExport) {
                    showWorkspaceMessage("Print/export permission is disabled for this account.");
                    return;
                  }
                  window.print();
                }}
              >
                Print
              </button>
            </div>
            {!canExport && (
              <p className="calculator-workspace-muted">
                Export is disabled for this employee account by default.
              </p>
            )}
          </aside>
        </section>

        <section className="calculator-power-strip">
          <div>
            <span className="calculator-workspace-kicker">Power Workflow</span>
            <p>
              Save outputs to the active manufacturing plan, compare related tools,
              and export the job calculation record for review or quoting.
            </p>
          </div>
          <div className="calculator-related-tools">
            {relatedTools.map((tool) => (
              <Link key={tool.id} to={`/calculator/${tool.id}`}>
                <span>{tool.badge}</span>
                {tool.name}
              </Link>
            ))}
          </div>
        </section>

        {relatedFormulas.length > 0 && (
          <section className="calculator-power-strip">
            <div>
              <span className="calculator-workspace-kicker">Related Formulas</span>
              <p>
                Reference formulas connected to this calculator. Open Formula Reference for the full searchable handbook.
              </p>
            </div>
            <div className="calculator-related-tools">
              {relatedFormulas.map((formula) => (
                <Link key={`${formula.category}-${formula.name}`} to={`/formula-reference?search=${encodeURIComponent(formula.name)}`}>
                  <span>REF</span>
                  {formula.name}
                </Link>
              ))}
            </div>
          </section>
        )}

        <CalculatorErrorBoundary resetKey={toolId}>
          <CalculatorComponent
            toolId={toolId}
            onSave={handleSaveCalculation}
            activeProjectId={selectedPlanId}
            activeProjectName={selectedPlan?.name || ""}
            activePlanId={selectedPlanId}
            activePlanName={selectedPlan?.name || ""}
            activeCustomer={selectedPlan?.customer || ""}
            activePartId={selectedPart?.id || ""}
            activePartName={selectedPart?.name || selectedPart?.partNumber || ""}
            savingCalculation={savingCalculation}
          />
        </CalculatorErrorBoundary>

        {saved && (
          <div className="save-notification">
            Calculation saved to {selectedPlan?.name || "manufacturing plan"} successfully!
          </div>
        )}

        {saveError && (
          <div
            style={{
              marginTop: "12px",
              padding: "12px 14px",
              borderRadius: "10px",
              background: "rgba(239,68,68,0.12)",
              border: "1px solid rgba(239,68,68,0.24)",
              color: "#ef4444",
              fontWeight: 600,
            }}
          >
            {saveError}
          </div>
        )}

        {selectedPlanCalculations.length > 0 && (
          <section className="calculator-history-panel">
            <div className="calculator-history-header">
              <div>
                <span className="calculator-workspace-kicker">Plan Calculation Record</span>
                <h3>
                  {selectedPlan?.name || "Manufacturing Plan"} saved calculations
                </h3>
              </div>
              <span>{selectedPlanCalculations.length} records</span>
            </div>
            <div className="calculator-history-list">
              {selectedPlanCalculations.slice(0, 8).map((calculation) => (
                <div key={calculation.id} className="calculator-history-item">
                  <div>
                    <strong>{calculation.toolName || calculation.toolId}</strong>
                    <p>{calculation.summary}</p>
                  </div>
                  <span>
                    {calculation.createdAt
                      ? new Date(calculation.createdAt).toLocaleDateString()
                      : "Saved"}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {workspaceMessage && (
          <div className="save-notification">
            {workspaceMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default Calculator;
